// frontend/src/components/audit/FiltersToolbar.tsx
import { useState, type ReactNode } from 'react'
import {
  Group, Stack, MultiSelect, TextInput, Select, Button, Box,
} from '@mantine/core'
import { DateTimePicker } from '@mantine/dates'

export interface AuditFilters {
  period_from?: Date | null
  period_to?: Date | null
  severity: string[]
  outcome: string[]
  actor_email: string
  action_glob: string
  resource_type: string[]
  client_ip: string
  request_id: string
  payload_search: string
}

export function defaultFilters(): AuditFilters {
  return {
    period_from: null, period_to: null,
    severity: [], outcome: [],
    actor_email: '', action_glob: '',
    resource_type: [], client_ip: '',
    request_id: '', payload_search: '',
  }
}

const PERIOD_PRESETS = [
  { value: '1h', label: 'Última hora' },
  { value: '24h', label: 'Últimas 24h' },
  { value: '7d', label: 'Últimos 7 dias' },
  { value: '30d', label: 'Últimos 30 dias' },
  { value: 'custom', label: 'Personalizado' },
]

function presetToRange(p: string): { from: Date | null; to: Date | null } {
  const now = new Date()
  if (p === '1h') return { from: new Date(now.getTime() - 3600_000), to: now }
  if (p === '24h') return { from: new Date(now.getTime() - 86400_000), to: now }
  if (p === '7d') return { from: new Date(now.getTime() - 7 * 86400_000), to: now }
  if (p === '30d') return { from: new Date(now.getTime() - 30 * 86400_000), to: now }
  return { from: null, to: null }
}

export function FiltersToolbar({
  value, onApply, tenantSelector,
}: {
  value: AuditFilters
  onApply: (f: AuditFilters) => void
  /**
   * Slot opcional renderizado acima dos filtros (linha 1). Usado pela
   * pagina Auditoria pra injetar o TenantMultiSelect quando o user tem
   * role admin/devops/gestor/auditor_global (cross-tenant view, S2-pt1).
   */
  tenantSelector?: ReactNode
}) {
  const [draft, setDraft] = useState<AuditFilters>(value)
  const [periodPreset, setPeriodPreset] = useState<string>('7d')

  const handlePeriod = (p: string | null) => {
    setPeriodPreset(p || 'custom')
    if (p && p !== 'custom') {
      const { from, to } = presetToRange(p)
      setDraft({ ...draft, period_from: from, period_to: to })
    }
  }

  return (
    <Box p="sm" style={{ position: 'sticky', top: 0, zIndex: 10, background: 'var(--mantine-color-body)' }}>
      <Stack gap="xs">
        {tenantSelector && <Group>{tenantSelector}</Group>}
        <Group>
          <Select
            label="Período" data={PERIOD_PRESETS}
            value={periodPreset} onChange={handlePeriod} w={180}
          />
          {periodPreset === 'custom' && (
            <>
              <DateTimePicker label="De" value={draft.period_from}
                onChange={(d) => setDraft({ ...draft, period_from: d })} w={200} />
              <DateTimePicker label="Até" value={draft.period_to}
                onChange={(d) => setDraft({ ...draft, period_to: d })} w={200} />
            </>
          )}
          <MultiSelect
            label="Severity"
            data={['info', 'warning', 'critical']}
            value={draft.severity}
            onChange={(v) => setDraft({ ...draft, severity: v })} w={200}
          />
          <MultiSelect
            label="Outcome"
            data={['success', 'failure', 'denied']}
            value={draft.outcome}
            onChange={(v) => setDraft({ ...draft, outcome: v })} w={200}
          />
        </Group>
        <Group>
          <TextInput
            label="Usuário (email)" value={draft.actor_email} w={250}
            onChange={(e) => setDraft({ ...draft, actor_email: e.currentTarget.value })}
          />
          <TextInput
            label="Ação (glob, ex: whatsapi:*)" value={draft.action_glob} w={250}
            onChange={(e) => setDraft({ ...draft, action_glob: e.currentTarget.value })}
          />
          <TextInput
            label="IP (exato ou CIDR)" value={draft.client_ip} w={180}
            onChange={(e) => setDraft({ ...draft, client_ip: e.currentTarget.value })}
          />
          <TextInput
            label="Request-ID (UUID)" value={draft.request_id} w={250}
            onChange={(e) => setDraft({ ...draft, request_id: e.currentTarget.value })}
          />
        </Group>
        <Group>
          <TextInput
            label="Busca em payload (free-text)"
            value={draft.payload_search} w={400}
            onChange={(e) => setDraft({ ...draft, payload_search: e.currentTarget.value })}
          />
          <Button onClick={() => onApply(draft)} mt={22}>Aplicar</Button>
          <Button variant="subtle" mt={22}
            onClick={() => { const d = defaultFilters(); setDraft(d); onApply(d); setPeriodPreset('7d') }}>
            Limpar
          </Button>
        </Group>
      </Stack>
    </Box>
  )
}
